import { Outlet, createRootRoute, createRoute, createRouter, useMatches } from '@tanstack/react-router';
import { busquedaDeAnalisis } from './lib/analisis.js';
import { busquedaDeControl } from './lib/control.js';
import { busquedaDeContabilidad } from './lib/libros.js';
import { busquedaDeGastos } from './lib/gastos.js';
import { pantallaPermitida } from './lib/permisos.js';
import { usePermisos } from './lib/sesion.js';
import { seguirTransiciones, tiposDeTransicion } from './lib/transiciones.js';
import { seguirHistoria } from './lib/volver.js';
import { PantallaDeError } from './ui/PantallaDeError.js';
import { Inicio } from './routes/Inicio.js';
import { Ajustes } from './routes/Ajustes.js';
import { AjustesNegocio } from './routes/AjustesNegocio.js';
import { Personal } from './routes/Personal.js';
import { Productos } from './routes/Productos.js';
import { ProductoNuevo } from './routes/ProductoNuevo.js';
import { ProductoDetalle } from './routes/ProductoDetalle.js';
import { CargaRapida } from './routes/CargaRapida.js';
import { Proveedores } from './routes/Proveedores.js';
import { ProveedorDetalle } from './routes/ProveedorDetalle.js';
import { Compras } from './routes/Compras.js';
import { Reponer } from './routes/Reponer.js';
import { Exportar } from './routes/Exportar.js';
import { NuevaCompra } from './routes/NuevaCompra.js';
import { ListasPrecio } from './routes/ListasPrecio.js';
import { Pos } from './routes/Pos.js';
import { Caja } from './routes/Caja.js';
import { EmpezarDia } from './routes/EmpezarDia.js';
import { TerminarTurno } from './routes/TerminarTurno.js';
import { CuadrarSemana } from './routes/CuadrarSemana.js';
import { RecibirLaCaja } from './routes/RecibirLaCaja.js';
import { Ventas } from './routes/Ventas.js';
import { Transferencias } from './routes/Transferencias.js';
import { Recargas } from './routes/Recargas.js';
import { Inventario } from './routes/Inventario.js';
import { Conteo } from './routes/Conteo.js';
import { Reempaque } from './routes/Reempaque.js';
import { Traslado } from './routes/Traslado.js';
import { Vencimientos } from './routes/Vencimientos.js';
import { Clientes } from './routes/Clientes.js';
import { ClienteDetalle } from './routes/ClienteDetalle.js';
import { Pedidos } from './routes/Pedidos.js';
import { NuevoPedido } from './routes/NuevoPedido.js';
import { EditarPedido } from './routes/EditarPedido.js';
import { Contabilidad } from './routes/Contabilidad.js';
import { Analisis } from './routes/Analisis.js';
import { Control } from './routes/Control.js';
import { ControlPersona } from './routes/ControlPersona.js';
import { Efectivo } from './routes/Efectivo.js';
import { Gastos } from './routes/Gastos.js';
import { PagosProveedores } from './routes/PagosProveedores.js';
import { DineroDueno } from './routes/DineroDueno.js';
import { Depositos } from './routes/Depositos.js';
import { Turnos } from './routes/Turnos.js';
import { RecargasAnteriores } from './routes/RecargasAnteriores.js';
import { Planilla } from './routes/Planilla.js';
import { Bitacora } from './routes/Bitacora.js';
import { Semilla } from './routes/Semilla.js';
import { Precios } from './routes/Precios.js';
import { Fotos } from './routes/Fotos.js';
import { Paquetes } from './routes/Paquetes.js';
import { Ofertas } from './routes/Ofertas.js';
import { Fiado } from './routes/Fiado.js';
import { Mas } from './routes/Mas.js';
import { Aprender } from './routes/Aprender.js';
import { Tutorial } from './tutorial/Tutorial.js';
import { SinPermiso } from './ui/SinPermiso.js';

/**
 * The tutorial lives here rather than beside `<RouterProvider>` in `App.tsx`.
 *
 * It drives the app — navigating to the screen a step lives on is most of what it does — so it
 * needs the router's context. Mounted as a sibling of the provider it rendered fine and simply
 * never went anywhere, which is a much quieter failure than it deserves to be.
 */
const rootRoute = createRootRoute({ component: Raiz });

/**
 * Every screen, behind the permission it needs (`lib/permisos.ts`) — here, once, rather than in
 * each screen, so a screen added later is covered before anybody remembers to cover it. An
 * address that matches no screen is left to the router, which says so.
 */
function Raiz() {
  const puede = usePermisos();
  const matches = useMatches();
  const ruta: string = matches[matches.length - 1]?.routeId ?? '__root__';
  const abierta = ruta === '__root__' || pantallaPermitida(ruta, puede);
  return (
    <>
      {abierta ? <Outlet /> : <SinPermiso ruta={ruta} />}
      <Tutorial />
    </>
  );
}

/**
 * Written out one by one rather than through a helper.
 *
 * The repetition was here to keep the path strings literal, on the theory that TanStack builds
 * its link types from them and a helper taking `path: string` would widen them away.
 *
 * That theory does not hold in this setup, and it is worth saying so plainly rather than
 * leaving the next person to trust it. Measured, not assumed: renaming a registered route
 * while thirty `<Link to>` sites still name the old one produces zero type errors, and so does
 * `<Link to="/ruta-que-no-existe">`. The `Register` augmentation below is in place; the paths
 * still do not narrow, most likely because `addChildren` over an array of separately
 * constructed routes loses the literals before they reach the register.
 *
 * So a dead link is silent here. `ops/tutorial-al-dia.mjs` checks every `to="/..."` in the app
 * against the paths registered below and fails the build on a mismatch, which is what actually
 * covers this today. Keep the literals anyway: they are what that script reads.
 */
const routeTree = rootRoute.addChildren([
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: Inicio }),
  createRoute({ getParentRoute: () => rootRoute, path: '/mas', component: Mas }),
  createRoute({ getParentRoute: () => rootRoute, path: '/aprender', component: Aprender }),
  createRoute({ getParentRoute: () => rootRoute, path: '/ajustes', component: Ajustes }),
  createRoute({ getParentRoute: () => rootRoute, path: '/ajustes/negocio', component: AjustesNegocio }),
  createRoute({ getParentRoute: () => rootRoute, path: '/ajustes/personal', component: Personal }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/productos',
    component: Productos,
    /** The products taken off sale, where they are put back. */
    validateSearch: (s: Record<string, unknown>): { vista?: 'desactivados' } =>
      s['vista'] === 'desactivados' ? { vista: 'desactivados' } : {},
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/productos/nuevo',
    component: ProductoNuevo,
    /**
     * A scanned code nobody recognised, carried over from «Crear un producto nuevo»; or the name
     * of something sold as a venta rápida, from «Ventas rápidas».
     */
    validateSearch: (s: Record<string, unknown>): { codigo?: string; nombre?: string } => {
      const r: { codigo?: string; nombre?: string } = {};
      if (typeof s['codigo'] === 'string') r.codigo = s['codigo'];
      if (typeof s['nombre'] === 'string') r.nombre = s['nombre'];
      return r;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/productos/$productoId',
    component: ProductoDetalle,
    /**
     * A page of its own for each product. «También lo lleva» goes from one product's page straight
     * to another's: drawn again in place, it showed the one left for an instant, its codes under
     * the new one's presentations, and kept whatever was half typed on it.
     */
    remountDeps: ({ params }) => params,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/carga-rapida', component: CargaRapida }),
  createRoute({ getParentRoute: () => rootRoute, path: '/precios', component: Precios }),
  createRoute({ getParentRoute: () => rootRoute, path: '/fotos', component: Fotos }),
  createRoute({ getParentRoute: () => rootRoute, path: '/paquetes', component: Paquetes }),
  createRoute({ getParentRoute: () => rootRoute, path: '/ofertas', component: Ofertas }),
  createRoute({ getParentRoute: () => rootRoute, path: '/proveedores', component: Proveedores }),
  createRoute({ getParentRoute: () => rootRoute, path: '/proveedores/$proveedorId', component: ProveedorDetalle }),
  createRoute({ getParentRoute: () => rootRoute, path: '/reponer', component: Reponer }),
  createRoute({ getParentRoute: () => rootRoute, path: '/exportar', component: Exportar }),
  createRoute({ getParentRoute: () => rootRoute, path: '/compras', component: Compras }),
  createRoute({ getParentRoute: () => rootRoute, path: '/compras/nueva', component: NuevaCompra }),
  createRoute({ getParentRoute: () => rootRoute, path: '/listas-precio', component: ListasPrecio }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/pos',
    component: Pos,
    /** «Cobrar» on the list of orders: the order to load into the sale, once, as the till's own list does. */
    validateSearch: (s: Record<string, unknown>): { pedido?: string } =>
      typeof s['pedido'] === 'string' ? { pedido: s['pedido'] } : {},
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/caja', component: Caja }),
  // The till's beginnings and endings, a step at a time: a shift started, ended, or taken over.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/caja/empezar',
    component: EmpezarDia,
    /** An order «Cobrar» sent to a till that was not open yet: the till loads it once open. */
    validateSearch: (s: Record<string, unknown>): { pedido?: string } =>
      typeof s['pedido'] === 'string' ? { pedido: s['pedido'] } : {},
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/caja/terminar', component: TerminarTurno }),
  // Every shift gone by, of which «Caja» keeps the latest ten.
  createRoute({ getParentRoute: () => rootRoute, path: '/caja/turnos', component: Turnos }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/caja/recibir',
    component: RecibirLaCaja,
    /** An order «Cobrar» sent to a till that was somebody else's: the till loads it once taken over. */
    validateSearch: (s: Record<string, unknown>): { pedido?: string } =>
      typeof s['pedido'] === 'string' ? { pedido: s['pedido'] } : {},
  }),
  // The owner's money, a step at a time: the day gone over.
  createRoute({ getParentRoute: () => rootRoute, path: '/dinero/semana', component: CuadrarSemana }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/ventas',
    component: Ventas,
    /**
     * «Vendido hoy» opens the same list, narrowed to the sales it adds up. Opened from the till,
     * `desde` sends «Volver» back there instead of home. `rapidas` is the owner's list of what
     * went through as a venta rápida.
     */
    validateSearch: (s: Record<string, unknown>) => {
      const r: { hoy?: true; desde?: 'pos'; rapidas?: true } = {};
      if (s['hoy'] === true) r.hoy = true;
      if (s['desde'] === 'pos') r.desde = 'pos';
      if (s['rapidas'] === true) r.rapidas = true;
      return r;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/transferencias',
    component: Transferencias,
    /** Opened from «Ventas», `desde` sends «Volver» back there; from the home screen's alert, home. */
    validateSearch: (s: Record<string, unknown>): { desde?: 'ventas' } =>
      s['desde'] === 'ventas' ? { desde: 'ventas' } : {},
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/recargas', component: Recargas }),
  createRoute({ getParentRoute: () => rootRoute, path: '/recargas/anteriores', component: RecargasAnteriores }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/inventario',
    component: Inventario,
    /** From the home screen's alert: only what is below zero, each a count waiting to be done. */
    validateSearch: (s: Record<string, unknown>): { negativos?: true } => (s['negativos'] === true ? { negativos: true } : {}),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/inventario/conteo', component: Conteo }),
  createRoute({ getParentRoute: () => rootRoute, path: '/inventario/reempaque', component: Reempaque }),
  createRoute({ getParentRoute: () => rootRoute, path: '/inventario/traslado', component: Traslado }),
  createRoute({ getParentRoute: () => rootRoute, path: '/vencimientos', component: Vencimientos }),
  createRoute({ getParentRoute: () => rootRoute, path: '/clientes', component: Clientes }),
  createRoute({ getParentRoute: () => rootRoute, path: '/clientes/$clienteId', component: ClienteDetalle }),
  createRoute({ getParentRoute: () => rootRoute, path: '/fiado', component: Fiado }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/clientes/pedidos/nuevo',
    component: NuevoPedido,
    /** Lets the client list jump straight into a new order without a route parameter. */
    validateSearch: (s: Record<string, unknown>): { cliente?: string } =>
      typeof s['cliente'] === 'string' ? { cliente: s['cliente'] } : {},
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/clientes/pedidos/$pedidoId/editar', component: EditarPedido }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/pedidos',
    component: Pedidos,
    /** The delivered or the cancelled instead of what is still to deliver; and the order just taken or changed, to point out. */
    validateSearch: (s: Record<string, unknown>): { vista?: 'entregados' | 'cancelados'; tomado?: string } => ({
      ...(s['vista'] === 'entregados' || s['vista'] === 'cancelados' ? { vista: s['vista'] } : {}),
      ...(typeof s['tomado'] === 'string' ? { tomado: s['tomado'] } : {}),
    }),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/contabilidad',
    component: Contabilidad,
    /** The tab and the days, so coming back from «Exportar», or from «Análisis», finds them (`busquedaDeContabilidad`). */
    validateSearch: busquedaDeContabilidad,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/analisis',
    component: Analisis,
    /** The tab and the period, so coming back from a client or a product finds them as they were. */
    validateSearch: busquedaDeAnalisis,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/control',
    component: Control,
    /** The days, so coming back from a person's page finds them as they were. */
    validateSearch: busquedaDeControl,
  }),
  // What a person's card adds up, each record of it opened (2026-10-23), over the same days.
  createRoute({ getParentRoute: () => rootRoute, path: '/control/$usuarioId', component: ControlPersona, validateSearch: busquedaDeControl }),
  createRoute({ getParentRoute: () => rootRoute, path: '/efectivo', component: Efectivo }),
  createRoute({ getParentRoute: () => rootRoute, path: '/efectivo/depositos', component: Depositos }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/gastos',
    component: Gastos,
    /**
     * The tab, so «Volver» from a whole list opened on one comes back to it; and «Nuevo gasto» on
     * the home screen, the form already open rather than the list and its button.
     */
    validateSearch: busquedaDeGastos,
  }),
  // The whole of two lists «Gastos» keeps the latest of: what suppliers were paid, the owner's money.
  createRoute({ getParentRoute: () => rootRoute, path: '/gastos/pagos', component: PagosProveedores }),
  createRoute({ getParentRoute: () => rootRoute, path: '/gastos/dueno', component: DineroDueno }),
  createRoute({ getParentRoute: () => rootRoute, path: '/planilla', component: Planilla }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/bitacora',
    component: Bitacora,
    /** The home screen's alerts open it on what they are about: refused records, or wrong PINs. */
    validateSearch: (s: Record<string, unknown>): { vista?: 'rechazos'; filtro?: 'sistema' } => ({
      ...(s['vista'] === 'rechazos' ? { vista: 'rechazos' as const } : {}),
      ...(s['filtro'] === 'sistema' ? { filtro: 'sistema' as const } : {}),
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/productos/semilla', component: Semilla }),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  // A screen that breaks while it draws says so in the screen's language, with the way home.
  defaultErrorComponent: PantallaDeError,
  // Every change of screen is a view transition; `lib/transiciones.ts` says which way it goes.
  defaultViewTransition: { types: tiposDeTransicion },
});
seguirHistoria(router);
seguirTransiciones(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
